'use client'

import * as Sentry from '@sentry/nextjs'
import clsx from 'clsx'
import { formatInTimeZone } from 'date-fns-tz'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import React, { useMemo, useState } from 'react'

import { getFullName } from '@/_lib/utils'
import { ITemplate, ITemplateTimeslot } from '@/_types'
import { pushAddToCart } from '@/_utils/analytics/datalayer'

/** Fixed zone so SSR (often UTC) matches browser hydration for displayed slot times. */
const TIMESLOT_DISPLAY_TZ = 'Europe/Bratislava'

interface IProps {
	closestTimeslots?: ITemplateTimeslot[] | null
	ambulanceSlug: string
	ambulanceName: string
}

export default function AAmbulanceContentTimeslots({
	closestTimeslots = [],
	ambulanceSlug,
	ambulanceName
}: IProps) {
	const t = useTranslations('pages.ambulance_detail.timeslots')
	const maxItems = 4

	const [showMore, setShowMore] = useState(false)
	const [isExpanded, setIsExpanded] = useState(true)

	const timeslotsToShow = useMemo(() => {
		if (!closestTimeslots) return []
		return showMore ? closestTimeslots : closestTimeslots?.slice(0, maxItems)
	}, [closestTimeslots, showMore])

	const handleToggleShowMore = (e: React.MouseEvent) => {
		e.preventDefault()
		setShowMore(prev => !prev)
	}

	// TODO: Vypytat od API aby posielal k workplace aj employee
	const getDoctorName = (template?: ITemplate) => {
		if (!template) return ''

		const employee = template.employee ?? template.workplace?.employee

		return employee ? getFullName(employee) : template.workplace?.name
	}

	const getTimeslotDate = (timeslot: ITemplateTimeslot) => {
		if (!timeslot.startTime) return ''

		try {
			return formatInTimeZone(timeslot.startTime, TIMESLOT_DISPLAY_TZ, 'dd.MM.yyyy')
		} catch (error) {
			Sentry.captureException(error)
			return ''
		}
	}

	const getTimeslotTime = (timeslot: ITemplateTimeslot) => {
		if (!timeslot.startTime) return ''

		try {
			return formatInTimeZone(timeslot.startTime, TIMESLOT_DISPLAY_TZ, 'HH:mm')
		} catch (error) {
			Sentry.captureException(error)
			return ''
		}
	}

	// In 2xn grid, check if item has next row
	const hasItemNextRow = (index: number) => {
		const totalRows = Math.ceil(timeslotsToShow.length / 2)
		const currentRow = Math.floor(index / 2)
		return currentRow < totalRows - 1
	}

	// In 2xn grid, check if item has next column
	const hasItemNextColumn = (index: number) => {
		return index % 2 === 0 && index < timeslotsToShow.length - 1
	}

	const handleTimeslotClick = (timeslot: ITemplateTimeslot) => {
		const template = timeslot.template
		if (!template) return

		pushAddToCart(
			{
				id: template.id,
				templateId: template.id,
				timeslotId: timeslot.id,
				name: template.name,
				type: 'template',
				price: template.price ?? 0
			},
			{ item_category2: ambulanceName, ambulanceSlug }
		)
	}

	// TODO: NO SLOTS MESSAGE

	return (
		<div
			className={clsx(
				'item_accord item_accord--timeslots accorditem under pdt-0lg pdt-30 bg-[#ccf0ea] rounded-[21px] px-4 ml-[-16px] mr-[-16px]',
				{
					expanded: isExpanded
				}
			)}
		>
			<h2
				className='item_accord_head accordhead mgt-0lg mgt-20'
				onClick={() => setIsExpanded(prev => !prev)}
			>
				<span className='smaller'>{t('closestTimeslots')}</span>
				{/* <svg className='sico'>
					<use href='/assets/layout/icons.svg#s_ar1_d'></use>
				</svg> */}
			</h2>

			<div className=''>
				<div>
					<div className='item_accord_content '>
						{!closestTimeslots || closestTimeslots.length === 0 ? (
							<div className='h-full min-h-[96px] flex items-center mgt-0lg mgt-20 px-4'>
								<p className='text-base'>
									{t('contactReceptionPhone')}{' '}
									<a href='tel:+421220302303' className='underline'>
										+421&nbsp;2&nbsp;20&nbsp;302&nbsp;303
									</a>{' '}
									{t('contactReceptionEmail')}{' '}
									<a href='mailto:recepcia@medante.sk' className='underline'>
										recepcia@medante.sk
									</a>
									.
								</p>
							</div>
						) : (
							<div className='deadlines_right !p-0 !w-full'>
								{timeslotsToShow.map((timeslot, index) => (
									<div
										className={clsx(
											'deadlines_item',
											hasItemNextRow(index) && 'deadlines_item--has-next-row',
											hasItemNextColumn(index) &&
												'deadlines_item--has-next-column'
										)}
										key={timeslot.id}
									>
										<span>{getDoctorName(timeslot?.template)}</span>
										<p>{timeslot?.template?.name}</p>

										<Link
											href={`/volne-terminy?type=template&id=${timeslot.id}&templateId=${timeslot.template.id}&ambulance=${ambulanceSlug}`}
											className='btn-calendar mgt-15'
											prefetch={false}
											onClick={() => handleTimeslotClick(timeslot)}
										>
											<span className='icon-calendar hide_mdm'></span>
											{getTimeslotDate(timeslot)} |{' '}
											{getTimeslotTime(timeslot)}
										</Link>
									</div>
								))}
							</div>
						)}

						{closestTimeslots && closestTimeslots.length > maxItems && (
							<div className='dflex justify-center mgt-15'>
								<Link
									href='#'
									title={showMore ? t('showLess') : t('showMore')}
									className='btn_basic type7 down'
									onClick={handleToggleShowMore}
									prefetch={false}
								>
									<span>
										<span>
											<svg className='sico'>
												{showMore ? (
													<use href='/assets/layout/icons.svg#s_ar1_u' />
												) : (
													<use href='/assets/layout/icons.svg#s_ar1_d' />
												)}
											</svg>
										</span>
										<span>
											<svg className='sico'>
												{showMore ? (
													<use href='/assets/layout/icons.svg#s_ar1_u' />
												) : (
													<use href='/assets/layout/icons.svg#s_ar1_d' />
												)}
											</svg>
										</span>
									</span>
								</Link>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
